import {
	confirmResetPassword,
	confirmSignIn,
	confirmSignUp,
	fetchAuthSession,
	fetchUserAttributes,
	getCurrentUser,
	resendSignUpCode,
	resetPassword,
	type SignInOutput,
	signIn,
	signOut,
	signUp,
	updatePassword,
} from 'aws-amplify/auth'

const wait = (ms: number) =>
	new Promise((resolve) => {
		setTimeout(resolve, ms)
	})

type LegacySignInUser = SignInOutput & {
	challengeName?: string
	challengeParam: {
		userAttributes: Record<string, string>
	}
	username: string
	signInUserSession: {
		accessToken: {
			payload: Record<string, any>
		}
	}
	attributes: Record<string, string> & { sub: string; email: string }
}

export type CognitoUser = LegacySignInUser

const mapSignInStepToChallengeName = (step?: string) => {
	if (step === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
		return 'CUSTOM_CHALLENGE'
	}
	if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
		return 'NEW_PASSWORD_REQUIRED'
	}
	return undefined
}

const normalizeGroups = (groups: unknown) => {
	if (Array.isArray(groups)) return groups
	if (typeof groups === 'string') return [groups]
	return []
}

async function fetchCompleteAuthState() {
	let lastError: unknown

	for (let attempt = 0; attempt < 5; attempt++) {
		try {
			const [user, session, attributes] = await Promise.all([
				getCurrentUser(),
				fetchAuthSession({ forceRefresh: attempt > 0 }),
				fetchUserAttributes(),
			])
			const payload = session.tokens?.accessToken?.payload ?? {}
			const sub =
				attributes.sub ??
				(typeof payload.sub === 'string' ? payload.sub : undefined) ??
				user.userId

			if (sub && attributes['custom:tenantid']) {
				return { user, payload, attributes, sub }
			}
		} catch (error) {
			lastError = error
		}

		await wait(150)
	}

	if (lastError) {
		throw lastError
	}

	const [user, session, attributes] = await Promise.all([
		getCurrentUser(),
		fetchAuthSession({ forceRefresh: true }),
		fetchUserAttributes(),
	])
	const payload = session.tokens?.accessToken?.payload ?? {}
	const sub =
		attributes.sub ??
		(typeof payload.sub === 'string' ? payload.sub : undefined) ??
		user.userId

	return { user, payload, attributes, sub }
}

export async function getCurrentIdentityId() {
	const session = await fetchAuthSession()
	return session.identityId
}

export async function getCurrentChedarAuthUser(): Promise<LegacySignInUser> {
	const { user, payload, attributes, sub } = await fetchCompleteAuthState()
	const groups = normalizeGroups(payload['cognito:groups'])

	return {
		isSignedIn: true,
		nextStep: { signInStep: 'DONE' },
		username: attributes.email ?? user.username,
		challengeParam: {
			userAttributes: attributes as Record<string, string>,
		},
		signInUserSession: {
			accessToken: {
				payload: {
					...payload,
					sub,
					'cognito:groups': groups,
				},
			},
		},
		attributes: {
			sub,
			email: attributes.email ?? user.username,
			...(attributes as Record<string, string>),
		},
	} as LegacySignInUser
}

export async function signInWithCustomChallenge(
	username: string,
	password: string,
	clientMetadata?: Record<string, string>
) {
	const result = (await signIn({
		username,
		password,
		options: {
			authFlowType: 'CUSTOM_WITH_SRP',
			clientMetadata,
		},
	})) as LegacySignInUser

	result.username = username
	result.challengeName = mapSignInStepToChallengeName(
		result.nextStep?.signInStep
	)
	result.challengeParam = {
		userAttributes: {},
	}
	result.signInUserSession = {
		accessToken: {
			payload: {},
		},
	}
	result.attributes = {
		sub: '',
		email: username,
	}
	return result
}

export async function confirmCustomChallenge(code: string) {
	return confirmSignIn({ challengeResponse: code })
}

export const Auth = {
	configure: (_config?: unknown) => undefined,
	currentAuthenticatedUser: getCurrentChedarAuthUser,
	currentCredentials: async () => {
		const session = await fetchAuthSession()
		return { identityId: session.identityId }
	},
	signIn: async (
		username: string,
		password: string,
		options?: {
			authFlowType?: string
			clientMetadata?: Record<string, string>
		}
	) => {
		return signInWithCustomChallenge(
			username,
			password,
			options?.clientMetadata
		)
	},
	sendCustomChallengeAnswer: async (_user: unknown, code: string) => {
		return confirmCustomChallenge(code)
	},
	completeNewPassword: async (
		_user: unknown,
		newPassword: string,
		_requiredAttributes?: Record<string, string>,
		clientMetadata?: Record<string, string>
	) => {
		return confirmSignIn({
			challengeResponse: newPassword,
			options: { clientMetadata },
		})
	},
	signOut,
	signUp: ({
		username,
		password,
		attributes,
		clientMetadata,
	}: {
		username: string
		password: string
		attributes?: Record<string, string>
		clientMetadata?: Record<string, string>
	}) => {
		return signUp({
			username,
			password,
			options: {
				userAttributes: attributes ?? {},
				clientMetadata,
			},
		})
	},
	confirmSignUp: (
		username: string,
		confirmationCode: string,
		options?: { clientMetadata?: Record<string, string> }
	) => {
		return confirmSignUp({
			username,
			confirmationCode,
			options,
		})
	},
	resendSignUp: (username: string) => {
		return resendSignUpCode({ username })
	},
	forgotPassword: (username: string) => {
		return resetPassword({ username })
	},
	forgotPasswordSubmit: (
		username: string,
		confirmationCode: string,
		newPassword: string
	) => {
		return confirmResetPassword({
			username,
			confirmationCode,
			newPassword,
		})
	},
	changePassword: (
		_user: unknown,
		oldPassword: string,
		newPassword: string
	) => {
		return updatePassword({ oldPassword, newPassword })
	},
}
